import { supabase } from '../lib/supabase';
import { mapPlatformMessageRow, validatePlatformMessageForm } from '../lib/platformMessagesUtils';

const SELECT_WITH_COUNTRY = `
  *,
  country:countries ( id, country_name, country_code )
`;

function rowToPayload(input, userId) {
  const errors = validatePlatformMessageForm(input);
  if (Object.keys(errors).length) {
    const err = new Error(Object.values(errors)[0]);
    err.validationErrors = errors;
    throw err;
  }

  return {
    title: input.title.trim(),
    body: input.body.trim(),
    message_type: input.messageType,
    country_id: input.countryId || null,
    starts_at: input.startsAt,
    ends_at: input.endsAt,
    is_active: input.isActive !== false,
    priority: Number(input.priority) || 0,
    link_url: input.linkUrl?.trim() || null,
    link_label: input.linkLabel?.trim() || null,
    updated_at: new Date().toISOString(),
    ...(userId && !input.id ? { created_by: userId } : {}),
  };
}

export const platformMessagesService = {
  async listActiveForViewer(countryId) {
    const now = new Date().toISOString();
    let query = supabase
      .from('platform_messages')
      .select(SELECT_WITH_COUNTRY)
      .eq('is_active', true)
      .lte('starts_at', now)
      .gte('ends_at', now)
      .order('priority', { ascending: false })
      .order('starts_at', { ascending: false });

    if (countryId) {
      query = query.or(`country_id.is.null,country_id.eq.${countryId}`);
    } else {
      query = query.is('country_id', null);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || []).map(mapPlatformMessageRow);
  },

  async listAll() {
    const { data, error } = await supabase
      .from('platform_messages')
      .select(SELECT_WITH_COUNTRY)
      .order('priority', { ascending: false })
      .order('starts_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(mapPlatformMessageRow);
  },

  async create(input, userId) {
    const payload = rowToPayload(input, userId);
    const { data, error } = await supabase
      .from('platform_messages')
      .insert(payload)
      .select(SELECT_WITH_COUNTRY)
      .single();

    if (error) throw error;
    return mapPlatformMessageRow(data);
  },

  async update(id, input) {
    const payload = rowToPayload({ ...input, id });
    const { data, error } = await supabase
      .from('platform_messages')
      .update(payload)
      .eq('id', id)
      .select(SELECT_WITH_COUNTRY)
      .single();

    if (error) throw error;
    return mapPlatformMessageRow(data);
  },

  async remove(id) {
    const { error } = await supabase.from('platform_messages').delete().eq('id', id);
    if (error) throw error;
  },

  /** Pays actifs pour le sélecteur admin (sans jointure regions). */
  async listCountriesForPicker() {
    const { data, error } = await supabase
      .from('countries')
      .select('id, country_name, country_code, is_active')
      .order('country_name', { ascending: true });

    if (error) throw error;

    return (data || [])
      .filter((c) => c.is_active !== false)
      .map((c) => ({
        id: c.id,
        countryName: c.country_name,
        countryCode: c.country_code,
      }));
  },
};

export default platformMessagesService;
